import { useEffect, useState } from 'react'
import { buildQrModulePath, qrViewBoxSide } from '../totp-qr-path'

type Props = {
  /** `otpauth://totp/...` payload to encode. */
  otpauthUri: string
  className?: string
}

type Matrix = {
  /** viewBox side in modules, including the quiet zone. */
  side: number
  /** One SVG path covering every dark module. */
  path: string
}

/**
 * QR code for an authenticator-app setup URI.
 *
 * Drawn as an SVG path rather than a raster so it stays sharp at any size and
 * on any pixel density — a phone camera reading a blurry code off a laptop
 * screen is the failure everyone remembers. The encoder is imported
 * dynamically: this is a once-per-account settings surface, and a static
 * import would put it in the chunk every screen loads.
 *
 * The shared secret is always rendered as text beside this, so a failure here
 * is degraded, never blocking.
 */
export function TotpQrCode({ otpauthUri, className }: Props) {
  const [matrix, setMatrix] = useState<Matrix | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setMatrix(null)
    setFailed(false)
    void (async () => {
      try {
        const { default: qrcode } = await import('qrcode-generator')
        // Type number 0 picks the smallest version that fits; 'M' error
        // correction is what every authenticator provisioning QR uses.
        const qr = qrcode(0, 'M')
        qr.addData(otpauthUri)
        qr.make()
        if (!cancelled) {
          setMatrix({ side: qrViewBoxSide(qr), path: buildQrModulePath(qr) })
        }
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [otpauthUri])

  if (failed) {
    return (
      <div
        className={`flex h-52 w-52 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500 ${className ?? ''}`}
        role="status"
      >
        The QR code could not be drawn. Use the setup key below instead — it works exactly the
        same way.
      </div>
    )
  }

  if (!matrix) {
    return (
      <div
        className={`h-52 w-52 shrink-0 animate-pulse rounded-xl border border-slate-200 bg-slate-100 ${className ?? ''}`}
        role="status"
        aria-label="Generating QR code"
      />
    )
  }

  return (
    <svg
      viewBox={`0 0 ${matrix.side} ${matrix.side}`}
      width={208}
      height={208}
      role="img"
      aria-label="QR code for setting up your authenticator app. If you cannot scan it, use the setup key shown alongside."
      className={`h-52 w-52 shrink-0 rounded-xl border border-slate-200 bg-white ${className ?? ''}`}
    >
      <rect width={matrix.side} height={matrix.side} fill="#ffffff" />
      <path d={matrix.path} fill="#0f172a" shapeRendering="crispEdges" />
    </svg>
  )
}
